// What this station did that the stock figures don't show yet (UI4S-204, D-057). The stock
// figures on a station change only when the office's figures sync down (D-046), so:
//
//   - pending stock: moves and write-offs saved here. Lot cards show the figures after them
//     ("Fresh 8 → 0"), and amounts are checked against those, so nobody moves or writes off
//     the same stock twice while the station is offline.
//   - blind holds: a location where a blind count was submitted keeps its figures hidden
//     until the server's figures after the count have synced; a discarded blind count keeps
//     them hidden for 30 minutes (D-057).
//
// Both are kept in `device_state` (they survive a reload) and end the same way: the
// command gets a final answer from the server, then a download comes in after that answer.
// The record of a move is written in the move's own transaction (a local effect).

import {
  DEVICE_STATE_KEYS,
  readDeviceState,
  writeDeviceState,
  type LocalEffect,
  type LocalTx,
  type StationData,
  type SyncEngineStatus,
} from '@enp-suite/data';
import { addQty, isNegative, isZero, qty, qtyToString, subQty } from '@enp/domain';
import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from '../data/live-query.js';
import { getStationData } from '../data/station-data-store.js';
import type { LotBalance, StationLot } from './lots.js';

/** A move or write-off saved on this station whose effect isn't in the figures yet. */
export interface PendingMovement {
  commandId: string;
  kind: 'move' | 'write_off';
  productId: string;
  lotId: string | null;
  locationId: string;
  /** The state the stock left (a move) or was written off from. */
  fromState: string;
  /** A move's target state. */
  toState?: string;
  qty: string;
  at: string;
  /** When the server gave the command its final answer. */
  answeredAt?: string;
}

/** A location whose figures stay hidden after a blind count there (D-057). */
export interface BlindHold {
  countId: string;
  locationId: string;
  at: string;
  /** The `count.submit` (none for a discarded count). */
  commandId?: string;
  answeredAt?: string;
  /** A discarded count: the hold ends at this time. */
  until?: string;
}

interface Awaiting {
  commandId?: string;
  answeredAt?: string;
  until?: string;
}

/** How long a discarded blind count keeps the location's figures hidden (D-057). */
export const DISCARD_HOLD_MS = 30 * 60_000;

/**
 * An answered command whose figures never came (a station with no download stream) stops
 * counting after this long, so the station never hides or adjusts figures for good.
 */
export const MAX_ANSWERED_WAIT_MS = 24 * 60 * 60_000;

/** How many records of each kind the station keeps (the oldest go first). */
const KEPT = 300;

// ------------------------------------------------------------------ pure rules (unit-tested)

/** The pending movement a saved command is (a state move or a write-off), else undefined. */
export function movementFromCommand(envelope: {
  command_id: string;
  type: string;
  payload: unknown;
  occurred_at: string;
}): PendingMovement | undefined {
  if (envelope.type !== 'stock.state_move' && envelope.type !== 'writeoff.request') return;
  const p = (envelope.payload ?? {}) as Record<string, unknown>;
  const productId = typeof p['product_id'] === 'string' ? p['product_id'] : undefined;
  const locationId = typeof p['location_id'] === 'string' ? p['location_id'] : undefined;
  const amount = typeof p['qty'] === 'string' ? p['qty'] : undefined;
  if (!productId || !locationId || !amount) return;
  const base = {
    commandId: envelope.command_id,
    productId,
    lotId: typeof p['lot_id'] === 'string' ? p['lot_id'] : null,
    locationId,
    qty: amount,
    at: envelope.occurred_at,
  };
  return envelope.type === 'stock.state_move'
    ? {
        ...base,
        kind: 'move',
        fromState: String(p['from_state'] ?? ''),
        toState: String(p['to_state'] ?? ''),
      }
    : { ...base, kind: 'write_off', fromState: String(p['state'] ?? '') };
}

/** Marks the records whose command is in `ids` as answered at `now`. Same list if none. */
export function markAnswered<T extends Awaiting>(
  list: readonly T[],
  ids: ReadonlySet<string>,
  now: Date,
): readonly T[] {
  if (!list.some((r) => r.commandId && !r.answeredAt && ids.has(r.commandId))) return list;
  return list.map((r) =>
    r.commandId && !r.answeredAt && ids.has(r.commandId)
      ? { ...r, answeredAt: now.toISOString() }
      : r,
  );
}

/** Nothing is waiting to upload: every command recorded here has been answered. */
export function markAllAnswered<T extends Awaiting>(list: readonly T[], now: Date): readonly T[] {
  if (!list.some((r) => r.commandId && !r.answeredAt)) return list;
  return list.map((r) =>
    r.commandId && !r.answeredAt ? { ...r, answeredAt: now.toISOString() } : r,
  );
}

/** Whether a record still stands: not answered and synced since, not expired. */
export function stillAwaiting(
  record: Awaiting,
  lastSyncedAt: Date | undefined,
  now: Date,
): boolean {
  if (record.until) return now.getTime() < Date.parse(record.until);
  if (!record.answeredAt) return true;
  const answered = Date.parse(record.answeredAt);
  if (lastSyncedAt && lastSyncedAt.getTime() > answered) return false;
  return now.getTime() - answered < MAX_ANSWERED_WAIT_MS;
}

/** The records that still stand. Same list if all do. */
export function prune<T extends Awaiting>(
  list: readonly T[],
  lastSyncedAt: Date | undefined,
  now: Date,
): readonly T[] {
  const kept = list.filter((r) => stillAwaiting(r, lastSyncedAt, now));
  return kept.length === list.length ? list : kept;
}

/** A place stock is: a product's lot (or lot-less stock) in a state at a location. */
export interface StockRef {
  productId: string;
  lotId: string | null;
  locationId: string;
  state: string;
}

const sameStock = (a: StockRef, b: Omit<StockRef, 'state'>) =>
  a.productId === b.productId &&
  (a.lotId ?? null) === (b.lotId ?? null) &&
  a.locationId === b.locationId;

/** The net change the pending movements make to one place (+ in, − out), as text. */
export function pendingDelta(ref: StockRef, pending: readonly PendingMovement[]): string {
  let delta = qty('0');
  for (const m of pending) {
    if (!sameStock(ref, m)) continue;
    if (m.fromState === ref.state) delta = subQty(delta, m.qty);
    if (m.kind === 'move' && m.toState === ref.state) delta = addQty(delta, m.qty);
  }
  return qtyToString(delta);
}

/** A place's quantity after the pending movements (never below 0). */
export function qtyAfterPending(
  ref: StockRef,
  current: string,
  pending: readonly PendingMovement[],
): string {
  const delta = pendingDelta(ref, pending);
  if (isZero(delta)) return qtyToString(qty(current));
  const after = addQty(current, delta);
  return isNegative(after) ? '0' : qtyToString(after);
}

/** Stock in one place, as the dialogs and cards list it. */
export interface StockItem {
  lotId: string | null;
  locationId: string;
  state: string;
  qty: string;
}

/**
 * Stock of one product after this station's pending movements, in the given order: changed
 * places carry the synced figure in `before`, and a state stock was moved into shows under
 * the place it came from (from 0). Places at 0 afterwards stay in the list (with `before`).
 */
export function stockAfterPending<S extends StockItem>(
  productId: string,
  stock: readonly S[],
  pending: readonly PendingMovement[],
): (S & { before?: string })[] {
  const mine = pending.filter((m) => m.productId === productId);
  if (mine.length === 0) return stock.map((s) => ({ ...s }));
  const out: (S & { before?: string })[] = [];
  const listed = (lotId: string | null, locationId: string, state: string) =>
    [...stock, ...out].some(
      (x) => (x.lotId ?? null) === lotId && x.locationId === locationId && x.state === state,
    );
  const place = (s: S, state: string, current: string) => {
    const ref = { productId, lotId: s.lotId ?? null, locationId: s.locationId, state };
    const now = qtyToString(qty(current));
    const after = qtyAfterPending(ref, now, mine);
    return after === now ? { ...s, state, qty: now } : { ...s, state, qty: after, before: now };
  };
  for (const s of stock) {
    out.push(place(s, s.state, s.qty));
    for (const m of mine) {
      if (m.kind !== 'move' || !m.toState || m.fromState !== s.state) continue;
      if ((m.lotId ?? null) !== (s.lotId ?? null) || m.locationId !== s.locationId) continue;
      if (listed(s.lotId ?? null, s.locationId, m.toState)) continue;
      out.push(place(s, m.toState, '0'));
    }
  }
  return out;
}

/** A balance as the station shows it: the figure after its pending movements. */
export interface ShownLotBalance extends LotBalance {
  lotId: string | null;
  lotCode: string | null;
  /** The synced figure, when pending movements change it. */
  before?: string;
}

/**
 * A lot's balances (or a product's, when each balance names its lot) after this station's
 * pending movements (see {@link stockAfterPending}).
 */
export function lotBalancesAfterPending(
  lot: Pick<StationLot, 'productId' | 'lotId' | 'lotCode' | 'balances'>,
  pending: readonly PendingMovement[],
): ShownLotBalance[] {
  const items = lot.balances.map((b) => ({
    ...b,
    lotId: b.lotId === undefined ? lot.lotId || null : b.lotId,
    lotCode: b.lotCode === undefined ? lot.lotCode || null : b.lotCode,
  }));
  return stockAfterPending(lot.productId, items, pending);
}

// ------------------------------------------------------------------ records in device_state

type Key = typeof DEVICE_STATE_KEYS.pendingStock | typeof DEVICE_STATE_KEYS.blindHolds;

async function readList<T>(tx: LocalTx, key: Key): Promise<T[]> {
  const list = await readDeviceState<T[]>(tx, key);
  return Array.isArray(list) ? list : [];
}

async function updateList<T>(
  tx: LocalTx,
  key: Key,
  change: (list: readonly T[]) => readonly T[],
  now: Date,
): Promise<void> {
  const list = await readList<T>(tx, key);
  const next = change(list);
  if (next === list) return;
  await writeDeviceState(tx, key, next.length > 0 ? next.slice(-KEPT) : null, now);
}

/** Runs several local effects in one command's transaction, in order. */
export function effects(...list: (LocalEffect | undefined)[]): LocalEffect {
  return async (tx, envelope) => {
    for (const effect of list) if (effect) await effect(tx, envelope);
  };
}

/** The local effect that records a move or write-off as pending (in its own transaction). */
export const recordPendingStock: LocalEffect = async (tx, envelope) => {
  const movement = movementFromCommand(envelope);
  if (!movement) return;
  await updateList<PendingMovement>(
    tx,
    DEVICE_STATE_KEYS.pendingStock,
    (list) => [...list.filter((m) => m.commandId !== movement.commandId), movement],
    new Date(),
  );
};

/** The local effect of submitting a blind count: its location stays hidden (D-057). */
export function holdAfterBlindCount(countId: string, locationId: string): LocalEffect {
  return (tx, envelope) =>
    updateList<BlindHold>(
      tx,
      DEVICE_STATE_KEYS.blindHolds,
      (list) => [
        ...list.filter((h) => h.countId !== countId),
        { countId, locationId, at: envelope.occurred_at, commandId: envelope.command_id },
      ],
      new Date(),
    );
}

/** A blind count discarded: its location stays hidden for 30 minutes (D-057). */
export async function holdAfterDiscard(
  countId: string,
  locationId: string,
  now: Date = new Date(),
): Promise<void> {
  const data = getStationData();
  if (!data) return;
  const until = new Date(now.getTime() + DISCARD_HOLD_MS).toISOString();
  await data.engine.writeTransaction((tx) =>
    updateList<BlindHold>(
      tx,
      DEVICE_STATE_KEYS.blindHolds,
      (list) => [
        ...list.filter((h) => h.countId !== countId),
        { countId, locationId, at: now.toISOString(), until },
      ],
      now,
    ),
  );
}

async function updateBoth(
  data: Pick<StationData, 'engine'>,
  change: <T extends Awaiting>(list: readonly T[]) => readonly T[],
  now: Date,
): Promise<void> {
  await data.engine.writeTransaction(async (tx) => {
    await updateList<PendingMovement>(tx, DEVICE_STATE_KEYS.pendingStock, change, now);
    await updateList<BlindHold>(tx, DEVICE_STATE_KEYS.blindHolds, change, now);
  });
}

/** Drops what has come back from the office (or expired). */
export function pruneAwaiting(
  data: Pick<StationData, 'engine'>,
  lastSyncedAt: Date | undefined = data.engine.getStatus().lastSyncedAt,
  now: Date = new Date(),
): Promise<void> {
  return updateBoth(data, (list) => prune(list, lastSyncedAt, now), now);
}

/**
 * Keeps the records up to date for as long as the station runs: answers mark them, a
 * download after the answer ends them, and a discarded count's hold runs out.
 */
export function watchAwaitingFigures(
  data: Pick<StationData, 'engine' | 'onAnswered'>,
  options: { intervalMs?: number } = {},
): () => void {
  const fail = (err: unknown) => console.error('The station could not update its figures', err);
  let lastPending: number | undefined;
  let lastSynced: number | undefined;
  const onStatus = (status: SyncEngineStatus) => {
    if (!status.ready) return;
    const now = new Date();
    // Nothing left to send (at start, or once the queue empties): every command recorded
    // here has its answer, even one whose answer came while the app was closed.
    if ((lastPending === undefined || lastPending > 0) && status.pendingCount === 0) {
      void updateBoth(data, (list) => markAllAnswered(list, now), now).catch(fail);
    }
    lastPending = status.pendingCount;
    const synced = status.lastSyncedAt?.getTime();
    if (synced !== lastSynced) {
      lastSynced = synced;
      void pruneAwaiting(data, status.lastSyncedAt, now).catch(fail);
    }
  };
  const stops = [
    data.onAnswered((ids) => {
      const now = new Date();
      void updateBoth(data, (list) => markAnswered(list, new Set(ids), now), now).catch(fail);
    }),
    data.engine.subscribe(onStatus),
  ];
  onStatus(data.engine.getStatus());
  const timer = setInterval(
    () => void pruneAwaiting(data).catch(fail),
    options.intervalMs ?? 60_000,
  );
  return () => {
    clearInterval(timer);
    for (const stop of stops) stop();
  };
}

// ------------------------------------------------------------------ React

const STATE_SQL = 'SELECT value FROM device_state WHERE id = ?';

function useList<T>(key: Key): T[] | undefined {
  const { rows } = useLiveQuery<{ value: string | null }>(STATE_SQL, [key]);
  return useMemo(() => {
    if (!rows) return undefined;
    const value = rows[0]?.value;
    if (!value) return [];
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }, [rows]);
}

const NONE: readonly PendingMovement[] = [];

/** Moves and write-offs saved here that the figures don't show yet (live). */
export function usePendingStock(): readonly PendingMovement[] {
  return useList<PendingMovement>(DEVICE_STATE_KEYS.pendingStock) ?? NONE;
}

/** Re-renders every `ms` (a discarded count's hold runs out on time). */
function useMinuteTick(active: boolean, ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [active, ms]);
  return now;
}

/** Locations whose figures are held hidden after a blind count there (live, D-057). */
export function useBlindHoldLocations(): ReadonlySet<string> {
  const holds = useList<BlindHold>(DEVICE_STATE_KEYS.blindHolds);
  const now = useMinuteTick((holds ?? []).some((h) => h.until));
  return useMemo(() => {
    const lastSyncedAt = getStationData()?.engine.getStatus().lastSyncedAt;
    const at = new Date(now);
    return new Set(
      (holds ?? []).filter((h) => stillAwaiting(h, lastSyncedAt, at)).map((h) => h.locationId),
    );
  }, [holds, now]);
}
